import { isConverted } from './calculateConvertedVolumeBRL.js'

// Crossfilter reducer behind every "Conversão (%)" figure on the Conversions
// tab (chart1, heatmap, bubble Y). One definition, so the three can't drift.
//
// The rate is measured by VOLUME, not by RFQ count: closing one R$ 50M ticket
// out of ten R$ 100k enquiries is not a 10% month. Since the tab only ever
// sees DEAL + NOTH.DONE (Conversions.vue::_getStoreRows), `vol` IS the
// negotiated volume and `volDeal` the converted one - the same
// converted/negotiated ratio the Overview "Taxa de Conversão" KPI reports.
//
// `cnt` is kept purely for the "obs" readouts in the tooltips; nothing
// computes a rate from it any more.
// `cntDeal` is separate from `cnt` on purpose - see hasDeals below.
export const emptyConversionValue = () => ({ cnt: 0, cntDeal: 0, vol: 0, volDeal: 0 })

// amountOf(row) must return the row's notional on ONE common currency - pass
// store.convertNotionalToBRL. Raw notional would silently add a USD ticket to
// a BRL one; the report mixes both, and the ratio does NOT cancel out when the
// currency mix differs between the DEAL and the NOTH.DONE rows of a bucket.
export function createConversionRateReducer (amountOf) {
  return {
    add: (p, v) => {
      const amount = amountOf(v)
      p.cnt += 1
      p.vol += amount
      if (isConverted(v)) { p.cntDeal += 1; p.volDeal += amount }
      return p
    },
    rem: (p, v) => {
      const amount = amountOf(v)
      p.cnt -= 1
      p.vol -= amount
      if (isConverted(v)) { p.cntDeal -= 1; p.volDeal -= amount }
      return p
    },
    init: emptyConversionValue,
  }
}

// `cnt` is the ONLY trustworthy emptiness test. `vol`/`volDeal` are floating
// sums maintained incrementally by crossfilter: when a filter removes every row
// of a group, adding then subtracting the same amounts does not land back on
// exactly 0 - a real group here left 7.28e-12 behind. That residue is truthy,
// and volDeal/vol over two residues yields a garbage rate (a fully filtered-out
// BUY band rendered a 20% bar and a bubble labelled "0.00"). `cnt` is integer
// arithmetic and is exactly 0.
const hasRows = (value) => !!value && value.cnt > 0

// `volDeal` needs its OWN counter, not `cnt`. `cnt` counts DEAL + NOTH.DONE, so
// a bucket that keeps its NOTH.DONE rows after a filter still reports cnt > 0
// while every DEAL row is gone - and then volDeal is a residue instead of a
// clean 0. Filtering to one counterparty produced volDeal = -1.49e-8 on a
// bucket with 28 rows, which drew a visible dot labelled "-0.00".
const hasDeals = (value) => !!value && value.cntDeal > 0

export const convertedVolumeOf = (value) => (hasDeals(value) ? value.volDeal : 0)

export const conversionRateOf = (value) => (hasRows(value) && value.vol ? convertedVolumeOf(value) / value.vol : 0)

// Merges partial values (e.g. the BUY and SELL halves of a heatmap cell) into
// one. Rates can only be combined through the volumes they came from -
// averaging two percentages weights a R$ 100k side the same as a R$ 50M one.
//
// Nothing needs to know whether a side filter is on: a filtered-out side
// reduces to {0,0,0} and drops out of the sum on its own, so the merged value
// is the single visible side's.
export const combineConversionValues = (...values) => values.reduce((acc, v) => ({
  cnt: acc.cnt + (v && v.cnt ? v.cnt : 0),
  cntDeal: acc.cntDeal + (v && v.cntDeal ? v.cntDeal : 0),
  vol: acc.vol + (v && v.vol ? v.vol : 0),
  volDeal: acc.volDeal + (v && v.volDeal ? v.volDeal : 0),
}), emptyConversionValue())

export const observationsOf = (value) => (value && value.cnt ? value.cnt : 0)
